import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Wrench, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { mcpApi, type AgentMcp } from "@/api/mcp";
import { cn } from "@/lib/utils";
import { ChatToolbarTooltip } from "@/components/chat/chat-toolbar-tooltip";

interface AgentToolsMenuProps {
  agentId: string | null;
  disabled?: boolean;
}

/**
 * Dropdown next to the chat input bar showing every MCP available to the
 * acting agent. Lets the user toggle the whole MCP on/off, or expand it to
 * toggle individual tools.
 *
 * Server-side: PATCH /mcp/agents/:agentId/:id mutates agent_mcps.enabled or
 * agent_mcps.allowedTools, then triggerMcpSync fires off a runtime reload.
 * agent_mcps.sync_status surfaces the result back here as a status pill.
 */
export function AgentToolsMenu({ agentId, disabled }: AgentToolsMenuProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const { data: mcps = [], isLoading } = useQuery({
    queryKey: ["agent-mcps", agentId],
    queryFn: () => (agentId ? mcpApi.listAgentMcps(agentId) : Promise.resolve([] as AgentMcp[])),
    enabled: !!agentId && open,
    // Keep refreshing while open so the sync_status badge moves from
    // pending → synced/skipped/failed as the runtime acks.
    refetchInterval: open ? 2000 : false,
  });

  const toggleEnabled = useMutation({
    mutationFn: (vars: { id: string; enabled: boolean }) =>
      mcpApi.updateAgentMcp(agentId!, vars.id, { enabled: vars.enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-mcps", agentId] }),
  });

  const setAllowedTools = useMutation({
    mutationFn: (vars: { id: string; allowedTools: string[] | null }) =>
      mcpApi.updateAgentMcp(agentId!, vars.id, { allowedTools: vars.allowedTools }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-mcps", agentId] }),
  });

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTool = (mcp: AgentMcp, tool: string) => {
    const available = mcp.availableTools ?? [];
    // Effective allowlist for this row: agent override first, else tenant override, else all available.
    const current = (mcp.allowedTools ?? mcp.tenantAllowedTools ?? available).filter((t) => available.includes(t));
    const isOn = current.includes(tool);
    const next = isOn ? current.filter((t) => t !== tool) : [...current, tool];
    // Storing `null` would mean "all tools allowed" — but the user just
    // explicitly chose a subset, so always store the explicit list.
    setAllowedTools.mutate({ id: mcp.id, allowedTools: next });
  };

  return (
    <div ref={wrapperRef} className="relative">
      <ChatToolbarTooltip label="Choose tools">
        <button
          onClick={() => setOpen((o) => !o)}
          disabled={disabled || !agentId}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border text-text-secondary transition-colors hover:border-text-secondary hover:bg-surface-hover hover:text-text-primary disabled:opacity-50"
          title="Choose tools this agent can use"
        >
          <Wrench size={13} />
        </button>
      </ChatToolbarTooltip>
      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-[min(340px,calc(100vw-2rem))] max-h-[400px] overflow-y-auto rounded-xl border border-border bg-white shadow-lg dark:bg-surface z-10 py-2">
          <div className="px-3 pb-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Tools available to this agent</p>
          </div>
          {!agentId ? (
            <p className="px-3 py-2 text-xs text-text-muted">Select an agent first.</p>
          ) : isLoading ? (
            <p className="px-3 py-2 text-xs text-text-muted">Loading…</p>
          ) : mcps.length === 0 ? (
            <p className="px-3 py-2 text-xs text-text-muted">
              No MCPs assigned. Add some from the agent settings.
            </p>
          ) : (
            mcps.map((mcp) => {
              const isExpanded = expanded.has(mcp.id);
              const available = mcp.availableTools ?? [];
              const allowed = mcp.allowedTools ?? mcp.tenantAllowedTools ?? available;
              const allowedSet = new Set(allowed);
              return (
                <div key={mcp.id} className="border-t border-border/40 first:border-t-0">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      onClick={() => toggleExpanded(mcp.id)}
                      className="flex h-5 w-5 shrink-0 items-center justify-center text-text-muted hover:text-text-primary"
                      disabled={available.length === 0}
                      title={available.length === 0 ? "No tool list cached for this MCP" : isExpanded ? "Hide tools" : "Show tools"}
                    >
                      {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </button>
                    <div className="flex flex-1 flex-col min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm text-text-primary font-medium">{mcp.tenantMcpName ?? "(unnamed)"}</span>
                        <SyncBadge status={mcp.syncStatus} error={mcp.syncError} />
                      </div>
                      <span className="text-[11px] text-text-muted">
                        {mcp.transportType ?? "?"}
                        {available.length > 0 && (
                          <> · {allowedSet.size}/{available.length} tools</>
                        )}
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      checked={mcp.enabled}
                      onChange={(e) => toggleEnabled.mutate({ id: mcp.id, enabled: e.target.checked })}
                      className="h-4 w-4 shrink-0 cursor-pointer accent-accent"
                      title={mcp.enabled ? "Disable MCP" : "Enable MCP"}
                    />
                  </div>
                  {isExpanded && available.length > 0 && (
                    <div className="px-3 pb-2 pl-10 flex flex-col gap-1">
                      {available.map((tool) => (
                        <label key={tool} className="flex cursor-pointer items-center gap-2 text-xs text-text-secondary">
                          <input
                            type="checkbox"
                            checked={allowedSet.has(tool)}
                            onChange={() => toggleTool(mcp, tool)}
                            className="h-3.5 w-3.5 cursor-pointer accent-accent"
                            disabled={!mcp.enabled}
                          />
                          <span className="truncate">{tool}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function SyncBadge({ status, error }: { status: AgentMcp["syncStatus"]; error: string | null }) {
  const tooltip = error ?? status;
  const cls = cn(
    "inline-flex h-4 items-center gap-1 rounded px-1 text-[10px] font-medium uppercase tracking-wide",
    status === "synced" && "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    status === "pending" && "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
    status === "skipped" && "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    status === "failed" && "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  );
  const Icon = status === "synced" ? CheckCircle2 : status === "failed" || status === "skipped" ? AlertCircle : Clock;
  return (
    <span className={cls} title={tooltip}>
      <Icon size={10} />
      {status}
    </span>
  );
}
